import axios from 'axios';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert base64 URL-safe string to Uint8Array (VAPID format)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function getPushPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function ensureServiceWorker() {
  // In dev, serviceWorker.js is served from /service-worker.js. CRA dev mode normally
  // doesn't register it, so we manually register here when push is requested.
  if (!('serviceWorker' in navigator)) throw new Error('Service Worker not supported');
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register('/service-worker.js');
  }
  await navigator.serviceWorker.ready;
  return reg;
}

export function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch { return 'UTC'; }
}

export async function subscribeToPush(reminderHour = 18, smartReminder = false, streakSaveEnabled = true) {
  if (!isPushSupported()) throw new Error('Push not supported in this browser');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notifications permission denied');

  const reg = await ensureServiceWorker();
  const { data: keyResp } = await axios.get(`${API}/push/vapid-public-key`);
  if (!keyResp?.public_key) throw new Error('VAPID public key not configured');

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyResp.public_key),
    });
  }
  await axios.post(`${API}/push/subscribe`, {
    subscription: sub.toJSON(),
    reminder_hour: reminderHour,
    smart_reminder: smartReminder,
    timezone: getUserTimezone(),
    streak_save_enabled: streakSaveEnabled,
  });
  logger.info('Push subscribed');
  return { subscription: sub, permission };
}

export async function unsubscribeFromPush() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    await axios.delete(`${API}/push/unsubscribe`, { data: { endpoint } });
  }
}

export async function sendTestPush() {
  const { data } = await axios.post(`${API}/push/test`);
  return data;
}

export async function getSuggestedHour() {
  const { data } = await axios.get(`${API}/push/suggested-hour`, {
    params: { tz: getUserTimezone() },
  });
  return data;
}

export async function getPushStatus() {
  const { data } = await axios.get(`${API}/push/status`);
  return data;
}
